import { zodResolver } from '@hookform/resolvers/zod';
import { Fade } from '@mui/material';
import type { NewSubsidiary, Subsidiary, SubsidiaryInput } from '@vy/types';
import { NewSubsidiarySchema } from '@vy/types';
import { isEmptyArray } from '@vy/utils';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../../../components/new-design/feedback/VyDialog';
import { useCreateSubsidiary } from '../../../../../../../hooks/requests/use-create-subsidiary';
import { queryKeys } from '../../../../../../../services/query-key/query-keys';
import { useDialog } from '../../../../../../../hooks/use-dialog';
import { useVyStepper } from '../../../../../../../hooks/use-vy-stepper';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import type { CreateSubsidiaryDialogStep } from '../../entities-tab-content-types/create-subsidiary-dialog-step';
import { CREATE_SUBSIDIARY_DIALOG_STEPS } from '../../entities-tab-content-types/create-subsidiary-dialog-step';
import { AccountInfoTabContent } from './create-subsidiary-dialog-components/AccountInfoTabContent';
import { BankInfoTabContent } from './create-subsidiary-dialog-components/BankInfoTabContent';

const CreateSubsidiaryDialogSteps: Record<CreateSubsidiaryDialogStep, (...args: any) => React.ReactElement> =
  {
    [CREATE_SUBSIDIARY_DIALOG_STEPS.accountInfo]: (isPrimarySelectable: boolean) => (
      <AccountInfoTabContent primarySelectable={isPrimarySelectable} />
    ),
    [CREATE_SUBSIDIARY_DIALOG_STEPS.bankInfo]: () => <BankInfoTabContent />,
  };

type CreateSubsidiaryDialogProps = {
  existingSubsidiaries: Subsidiary[];
  children: React.ReactElement;
};

export const CreateSubsidiaryDialog: React.FC<CreateSubsidiaryDialogProps> = ({
  children,
  existingSubsidiaries,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const createSubsidiaryMutation = useCreateSubsidiary();
  const client = useQueryClient();
  const { currentStep, nextStep, previousStep, isFirstStep, isFinalStep, goToStep } = useVyStepper(
    CREATE_SUBSIDIARY_DIALOG_STEPS.accountInfo,
    CREATE_SUBSIDIARY_DIALOG_STEPS,
  );

  const canBePrimary =
    isEmptyArray(existingSubsidiaries) || !existingSubsidiaries.some(({ isPrimary }) => isPrimary);

  const methods = useForm<SubsidiaryInput, unknown, NewSubsidiary>({
    resolver: zodResolver(NewSubsidiarySchema),
  });

  const handleSubmit = methods.handleSubmit(async (subsidiary) => {
    await runMutation(
      createSubsidiaryMutation,
      { subsidiary },
      {
        pending: 'Creating subsidiary...',
        error: 'Failed to create subsidiary',
        success: 'Subsidiary created',
      },
      {
        onSuccess: async () => {
          await client.invalidateQueries(queryKeys.subsidiaries());
        },
      },
    );
    handleClose();
    goToStep(CREATE_SUBSIDIARY_DIALOG_STEPS.accountInfo);
    methods.reset();
  });

  return (
    <>
      {React.cloneElement(children, {
        onClick: handleOpen,
      })}

      <VyDialog
        onSubmit={handleSubmit}
        open={isDialogOpen}
        onClose={handleClose}
        label="Create new subsidiary"
        ctaText={isFinalStep ? 'Create' : 'Next'}
        cancelButtonText={!isFirstStep ? 'Back' : undefined}
        cancelButtonProps={{
          onClick: () => {
            previousStep();
          },
        }}
        ctaButtonProps={{
          type: isFinalStep ? 'submit' : 'button',
          onClick: !isFinalStep
            ? async () => {
                nextStep();
              }
            : handleSubmit,
        }}
        ctaDisabled={methods.formState.isSubmitting}
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit}>
            {Object.entries(CreateSubsidiaryDialogSteps).map(([dialogStep, render]) => (
              <Fade
                key={dialogStep}
                hidden={currentStep.toString() !== dialogStep}
                in={currentStep.toString() === dialogStep}
              >
                <div className="h-full w-full">
                  {currentStep.toString() === dialogStep && render(canBePrimary)}
                </div>
              </Fade>
            ))}
          </form>
        </FormProvider>
      </VyDialog>
    </>
  );
};
